import { TApiaFilter, TApiaFilterValue } from '@apia/util';
import { ForwardedRef } from 'react';
import * as React from 'react';
import { Input, Select } from '@apia/theme';
import { getVariant } from '@apia/theme';
import {
  TNumberInputChangeEvent,
  NumberInput,
  getFieldErrorStyles,
} from '../forms';
import { ApiaDateFilter } from './ApiaDateFilter';

export interface IApiaFilter {
  filter: TApiaFilter;
  onBlur?: (ev: TApiaFilterValue) => void;
  onChange?: (
    ev: TApiaFilterValue,
  ) => void | boolean | string | Promise<boolean | string>;
  onPressEnter?: (ev: TApiaFilterValue) => void;
}

const NoMemoApiaFilter = (
  { filter, onChange, onBlur, onPressEnter }: IApiaFilter,
  ref: React.Ref<HTMLInputElement | HTMLSelectElement>,
) => {
  const [value, setValue] = React.useState<TApiaFilterValue>(
    filter.currentValue ?? '',
  );

  const handleClick = React.useCallback((ev: React.MouseEvent) => {
    ev.stopPropagation();
  }, []);

  const [lastEmittedValue, setLastEmittedValue] =
    React.useState<TApiaFilterValue>(filter.currentValue);

  const filterCurrentValue = filter?.currentValue;
  React.useEffect(() => {
    if (filterCurrentValue === '') {
      setValue(String(filterCurrentValue));
      setLastEmittedValue(String(filterCurrentValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter?.deleteFiltersTimestamp]);

  if (filter) {
    const handleChange = (
      ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    ) => {
      if (
        (onChange && onChange(String(ev.target.value)) !== false) ||
        !onChange
      )
        setValue(String(ev.target.value));
    };
    const handleNumberChange = (ev: TNumberInputChangeEvent) => {
      if ((onChange && onChange(String(ev.value)) !== false) || !onChange)
        setValue(String(ev.value));
    };
    const handleBlur = (
      ev:
        | React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
        | React.KeyboardEvent,
    ) => {
      if (onBlur && lastEmittedValue !== value) {
        setLastEmittedValue(value);
        onBlur((ev.target as HTMLInputElement).value);
      }
    };

    const sortedOptions = filter.sortCombo
      ? [...(filter.options ?? [])].sort((a, b) => (a.label > b.label ? 1 : -1))
      : filter.options;
    const options = sortedOptions?.map((option) => {
      return (
        <option value={option.value} key={option.value ?? option.label ?? ' '}>
          {option.label}
        </option>
      );
    });

    if (filter.type === 'date' || filter.type === 'D')
      return (
        <ApiaDateFilter
          filter={filter}
          onChange={onChange}
          onBlur={onBlur}
          onPressEnter={onPressEnter}
          ref={ref}
        />
      );

    if (filter.type === 'apiaNumber') {
      return (
        <NumberInput
          aria-readonly={filter.readonly}
          readOnly={filter.readonly}
          value={value as string}
          placeholder={filter.placeholder}
          onChange={handleNumberChange}
          onKeyDown={(ev) => {
            if (ev.key.toLowerCase() === 'enter') {
              handleBlur(ev);
              if (onPressEnter)
                onPressEnter((ev.target as HTMLInputElement).value);
            }
          }}
          onClick={handleClick}
          {...getVariant('input')}
          className={`filter ${filter.error ? 'requiredFilter__Border' : ''}`}
          title={filter.title ?? filter.toolTip ?? String(filter.id)}
          ref={ref as ForwardedRef<HTMLInputElement>}
          aria-label={filter.title ?? filter.toolTip ?? String(filter.id)}
          name={filter.id as string}
        />
      );
    }

    return filter.options ? (
      <Select
        aria-readonly={filter.readonly}
        disabled={filter.readonly}
        onChange={handleChange}
        onBlur={handleBlur}
        value={value}
        className={`"filter" ${filter.error ? 'requiredFilter__Border' : ''}`}
        title={filter.title ?? filter.toolTip ?? String(filter.id)}
        ref={ref as ForwardedRef<HTMLSelectElement>}
        aria-label={filter.title ?? filter.toolTip ?? String(filter.id)}
        onClick={handleClick}
        sx={getFieldErrorStyles(!filter.error)}
        name={filter.id as string}
      >
        {options}
      </Select>
    ) : (
      <Input
        aria-readonly={filter.readonly}
        readOnly={filter.readonly}
        value={value}
        type={(filter.type === 'N' ? 'number' : filter.type) ?? 'text'}
        placeholder={filter.placeholder}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={(ev) => {
          if (ev.key.toLowerCase() === 'enter') {
            handleBlur(ev);
            if (onPressEnter)
              onPressEnter((ev.target as HTMLInputElement).value);
          }
        }}
        onClick={handleClick}
        {...getVariant('input')}
        className={`filter ${filter.error ? 'requiredFilter__Border' : ''}`}
        title={filter.title ?? filter.toolTip ?? String(filter.id)}
        ref={ref as ForwardedRef<HTMLInputElement>}
        aria-label={filter.title ?? filter.toolTip ?? String(filter.id)}
        name={filter.id as string}
      />
    );
  }
  return null;
};

export const ApiaFilter = React.memo(React.forwardRef(NoMemoApiaFilter));
